import React, { useState } from 'react';
import './ReviewTable.scss';
import FontAwesomeIcon from '../Icon';
import { get } from 'lodash';
import classNames from 'classnames';

type SortDirection = '' | 'ascending' | 'descending';

type ReviewTableRowData = Record<string, any>;

interface ReviewTableColumn {
  label: React.ReactNode;
  accessor?: string;
  sortable?: boolean;
  center?: boolean;
  xsHide?: boolean;
  cell?: (row: ReviewTableRowData) => React.ReactNode;
}

interface ReviewTableSortState {
  accessor: string;
  direction: SortDirection;
}

interface ReviewTableProps {
  columns: ReviewTableColumn[];
  data: ReviewTableRowData[];
  simplifiedMobileWalkThrough?: boolean;
}

const ReviewTable = ({ columns, data, simplifiedMobileWalkThrough }: ReviewTableProps) => {
  const [sortState, setSortState] = useState<ReviewTableSortState>({
    accessor: '',
    direction: '',
  });

  const { accessor, direction } = sortState;

  const sorted = data.slice().sort((a, b) => {
    if (get(a, accessor) > get(b, accessor)) {
      return -1;
    }
    if (get(b, accessor) > get(a, accessor)) {
      return 1;
    }
    return 0;
  });

  const results = direction === 'ascending' ? sorted.reverse() : sorted;

  return (
    <table className="review-table">
      <thead>
        <tr>
          {columns.map((c, i) => (
            <th
              key={i}
              className={
                classNames(
                  'header-cell',
                  {
                    'centered': c.center,
                    'hide-xs': c.xsHide,
                    'description-column': c.label === 'Description',
                    'ticket-number-column': c.label === 'Ticket Number' || c.label === 'Room',
                    'simplified': simplifiedMobileWalkThrough === true,
                  }
                )
              }
            >
              {c.sortable ? (
                <button
                  className="sort-button"
                  onClick={() => setSortState({
                      accessor: c.accessor ?? '',
                      direction: direction === 'ascending'
                        ? 'descending'
                        : 'ascending',
                  })}
                >
                {c.label}
                {c.accessor === accessor && (
                  <FontAwesomeIcon
                    glyph={direction === 'ascending' ? 'caret-up' : 'caret-down'}
                    weight="solid"
                  />
                )}
              </button>
              ) : c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {results.map((d, i) => (
          <tr
            key={i}
            className="table-row"
          >
            {columns.map((c, x) => (
              <td
                key={x}
                className={
                  classNames(
                    'data-cell',
                    {
                      'centered': c.center,
                      'hide-xs': c.xsHide,
                      'description-column': c.label === 'Description',
                      'ticket-number-column': c.label === 'Ticket Number' || c.label === 'Room',
                      'simplified': simplifiedMobileWalkThrough === true,
                    }
                  )
                }
              >
                {c.cell
                  ? c.cell(d)
                  : get(d, c.accessor ?? '')}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default ReviewTable;